"use client";
import { useAppStore, selectUser, AppStoreState } from "@/store/appStore";
import { TransactionContextPanel } from "./components/transaction-context-panel";
import { TransactionExtended, UserExtended } from "@/types";
import { BaseHTMLAttributes, FC, useEffect, useMemo, useState } from "react";
import { TransactionContext } from "./context";

import { List } from "../components/list";

// eslint-disable-next-line @typescript-eslint/no-require-imports
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
dayjs.extend(advancedFormat);

import "./transactions.style.css";
import _tailwindConfig from "_tailwind.config";
import { ContentHeader } from "@/app/report/components";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus } from "lucide-react";
import { Content } from "../components/page/components";
import { WorkflowStatus } from "@prisma/client";
import { CurrencyCode } from "@/components/ui";
import clsx from "clsx";

interface TransactionProps
  extends BaseHTMLAttributes<Pick<HTMLDivElement, "className">> {
  user?: UserExtended;
}

const Transactions: FC<TransactionProps> = ({ className }) => {
  const user: UserExtended | null = useAppStore(selectUser);
  const selectedCompany = useAppStore((s: AppStoreState) => s.selectedCompany);
  const isLoading = useAppStore((s: AppStoreState) => s.loading?.transactions);

  // Get transactions directly from the store instead of user.selectedReport
  const storeTransactions = useAppStore((s: AppStoreState) => s.transactions);

  const transactions = useMemo(() => {
    // Try multiple sources to get transactions - either from store or from user.selectedReport
    let transactionsArray = storeTransactions;

    // If not in store, try user.selectedReport
    if (!transactionsArray || transactionsArray.length === 0) {
      transactionsArray = user?.selectedReport?.transactions || [];
    }

    return transactionsArray as TransactionExtended[];
  }, [user, storeTransactions]);
  const [selectedTransaction, setSelectedTransaction] =
    useState<TransactionExtended | null>(transactions?.[0] || null);

  // Keep track of transaction data
  useEffect(() => {
    // Component mounted or data changed
  }, [user, transactions]);

  // Reload transactions when company changes
  useEffect(() => {
    // If you need to reload transactions when company changes, implement here
  }, [selectedCompany?.id]);

  return (
    <div className={clsx("page-transaction flex w-full", className)}>
      <TransactionContext.Provider
        value={{
          selectedTransaction: selectedTransaction || undefined,
          setSelectedTransaction,
        }}
      >
        <Content className="ml-0 pl-0 overflow-auto border-l-0">
          <div className="h-full flex flex-col overflow-hidden">
            {/* Always render header structure, show skeletons only if specifically loading transactions */}
            {isLoading ? (
              <div className="flex flex-col space-y-2 mb-4">
                <Skeleton className="h-8 w-32" />
                <Skeleton className="h-6 w-24" />
              </div>
            ) : (
              <ContentHeader
                title="Transactions"
                subtitle="75% Matched"
                actions={[
                  {
                    icon: Plus,
                    text: "Add Transaction",
                  },
                ]}
              />
            )}

            {/* Transaction list or skeleton placeholder */}
            <div className="transaction-list-container flex-1 overflow-hidden h-[calc(100%-50px)]">
              {isLoading ? (
                <div className="flex flex-col gap-3 px-4">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-4 p-3 border border-border/30 rounded-md"
                    >
                      <Skeleton className="h-10 w-10 rounded-full" />
                      <div className="flex-1 flex flex-col gap-2">
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-4 w-1/2" />
                      </div>
                      <Skeleton className="h-6 w-16" />
                    </div>
                  ))}
                </div>
              ) : !user || !transactions || transactions.length === 0 ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "300px",
                    fontSize: "1.25rem",
                    color: "#888",
                  }}
                >
                  No Transactions
                </div>
              ) : (
                <List
                  className="h-full"
                  groupBy={(transaction) => {
                    if (!transaction) return "Unknown";
                    return dayjs(
                      transaction.bookingDate || transaction.date || new Date()
                    ).format("MMM, D");
                  }}
                  sortGroupBy={(a, b) => {
                    if (!a || !b) return 0;
                    return (
                      dayjs(b, "MMMM, D, YYYY").unix() -
                      dayjs(a, "MMMM, D, YYYY").unix()
                    );
                  }}
                  title={(transaction) => {
                    if (!transaction) return "Unknown";
                    return dayjs(
                      transaction.bookingDate || transaction.date || new Date()
                    ).format("MMM D");
                  }}
                  items={transactions
                    .filter((transaction) => !!transaction && !!transaction.id)
                    .map((transaction) => {
                      const remittance =
                        transaction?.remittanceInformation || "";
                      const info = transaction.additionalInformation
                        ? `${transaction.additionalInformation
                            .toString()
                            .toLowerCase()
                            .trim()}`
                        : "";
                      const description =
                        remittance && info
                          ? remittance + ` (${info})`
                          : info || remittance;

                      return {
                        label:
                          transaction &&
                          transaction.bankInstitution &&
                          transaction.bankInstitution.name,
                        description,
                        amount: transaction.amount ?? 0,
                        currency: transaction.currency as CurrencyCode,
                        isSelected: selectedTransaction?.id === transaction.id,
                        status: transaction.workflowStatus as WorkflowStatus,
                        onClick: () => {
                          setSelectedTransaction(transaction);
                        },
                        image: transaction.invoices[0]?.seller
                          .corporateWebsiteURL
                          ? {
                              type: "logo",
                              source:
                                transaction.invoices[0]?.seller
                                  .corporateWebsiteURL ??
                                transaction.bankInstitution.logo,
                              fallback:
                                transaction.invoices[0]?.seller?.shortCompanyName?.charAt(
                                  0
                                ) || "N/A",
                            }
                          : {
                              type: "avatar",
                              source: transaction.bankInstitution.logo,
                              fallback:
                                transaction.bankInstitution.name.charAt(0) ||
                                "N/A",
                            },
                        date: transaction.bookingDateTime || undefined,
                      };
                    })}
                />
              )}
            </div>
          </div>
        </Content>

        {/* Always render the sidebar panel structure, with conditional content */}
        <div className="transaction-sidebar w-[320px] min-h-screen bg-background">
          {isLoading ? (
            <div className="p-4 flex flex-col gap-4">
              <Skeleton className="h-6 w-40 mb-2" />
              <Skeleton className="h-4 w-32 mb-2" />
              <Skeleton className="h-4 w-24 mb-2" />
              <Skeleton className="h-10 w-full mb-2" />
              <Skeleton className="h-4 w-3/4 mb-2" />
              <Skeleton className="h-4 w-1/2 mb-2" />
            </div>
          ) : !user ? (
            <div className="p-4 flex items-center justify-center h-full text-muted-foreground">
              Select a transaction to view details
            </div>
          ) : (
            <TransactionContextPanel />
          )}
        </div>
      </TransactionContext.Provider>
    </div>
  );
};

export { Transactions };
